import { NAV_DARK_FOCUS_RING } from '@/components/scaffold/layout/nav-dark';
import { useAuth } from '@/contexts/auth-context';
import { useDiscoveryQuota } from '@/hooks/queries/use-discovery-quota';
import { useGiftingInvitationQuota } from '@/hooks/queries/use-gifting-invitation-quota';
import { usePlanFeaturesQuery } from '@/hooks/queries/use-plan-features-query';
import { useProfileUnlockQuota } from '@/hooks/queries/use-profile-unlock-quota';
import { useRecommendationsQuery } from '@/hooks/queries/use-recommendations-query';
import { usePaywall } from '@/hooks/use-paywall';
import { formatResetDay } from '@/libs/utils/format-reset-day';
import { Box, Button, chakra, Flex, Text } from '@chakra-ui/react';
import { PlanTier } from '@fluenceur/types';
import { ArrowRight } from 'iconoir-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  countedQuotas,
  mostUsedQuota,
  nextReset,
  quotaRatio,
  quotaTone,
  type FreePlanQuota,
  type QuotaTone,
} from './free-plan-quotas';

/** Bar colours on the dark card, then on the light detail panel. */
const DARK_BAR: Record<QuotaTone, string> = {
  normal: 'navDark.aiText',
  warning: 'accent.warning',
  exhausted: 'red.400',
};
const LIGHT_BAR: Record<QuotaTone, string> = {
  normal: 'lilac.600',
  warning: 'yellow.500',
  exhausted: 'red.700',
};

/** How long the panel waits before closing, so the pointer can cross the gap to it. */
const CLOSE_DELAY_MS = 120;

const QuotaBar = ({
  quota,
  colors,
  track,
}: {
  quota: FreePlanQuota;
  colors: Record<QuotaTone, string>;
  track: string;
}) => (
  <Box h="4px" rounded="full" bg={track} overflow="hidden" aria-hidden="true">
    <Box
      h="full"
      rounded="full"
      bg={colors[quotaTone(quota)]}
      w={`${Math.round(quotaRatio(quota) * 100)}%`}
      transition="width .3s"
      data-testid={`free-plan-bar-${quota.key}`}
    />
  </Box>
);

/**
 * Raised block above the menu footer for a brand on the free plan. Reads the
 * month's allowances and hands them to {@link FreePlanCardView}.
 *
 * Shown on the free plan only; nothing shows while the plan is loading, so a
 * paying brand never sees it flash. An allowance still loading, or one the
 * plan does not count, is left out.
 */
export const FreePlanCard: React.FC = () => {
  const { user } = useAuth();
  const { planTier } = usePlanFeaturesQuery();
  const { openPlans } = usePaywall();
  const isFree = planTier === PlanTier.FREE;
  const brandId = isFree ? (user?.currentBrandId ?? '') : '';
  // An empty brand id keeps the read disabled for every other plan.
  const { data: batch } = useRecommendationsQuery(brandId);
  const searches = useDiscoveryQuota(brandId || undefined);
  const profileUnlocks = useProfileUnlockQuota();
  const giftingInvitations = useGiftingInvitationQuota();

  if (!isFree) return null;

  return (
    <FreePlanCardView
      quotas={countedQuotas({
        searches,
        profileUnlocks,
        giftingInvitations,
        recommendations: batch?.quota,
      })}
      onUpgrade={() => openPlans('essential')}
    />
  );
};

/**
 * The card itself: the plan name and the allowance closest to its end, and on
 * hover, focus or click a panel listing every allowance with the day they
 * start again and a way to a paid plan. A bar turns amber from 80 % and red
 * once the allowance is used.
 */
export const FreePlanCardView = ({
  quotas,
  onUpgrade,
  defaultOpen = false,
}: {
  quotas: FreePlanQuota[];
  onUpgrade: () => void;
  /** Opens the panel on mount, for the design review. */
  defaultOpen?: boolean;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const panelId = useId();
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const top = mostUsedQuota(quotas);
  const resetDay = formatResetDay(nextReset(quotas), t('common.locale'), { short: true });

  const show = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hideSoon = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  return (
    <Box
      position="relative"
      w="full"
      onMouseEnter={show}
      onMouseLeave={hideSoon}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) hideSoon();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      {open && quotas.length > 0 && (
        <Box
          id={panelId}
          position="absolute"
          left={0}
          right={0}
          bottom="100%"
          pb="8px"
          zIndex={5}
          data-testid="free-plan-panel"
        >
          <Flex
            direction="column"
            gap="12px"
            p="14px"
            bg="white"
            borderWidth="1px"
            borderColor="cardBorder"
            rounded="16px"
          >
            <Text fontSize="15px" fontWeight="semibold" letterSpacing="-0.01em" color="ink">
              {t('navigation.freePlanCard.title')}
            </Text>
            <Flex direction="column" gap="10px">
              <Flex align="baseline" justify="space-between" gap={2}>
                <Text
                  fontSize="10.5px"
                  fontWeight="semibold"
                  letterSpacing="0.1em"
                  textTransform="uppercase"
                  color="inkSubtle"
                >
                  {t('navigation.freePlanCard.thisMonth')}
                </Text>
                {resetDay && (
                  <Text fontSize="11px" color="inkMuted">
                    {t('navigation.freePlanCard.resetsOn', { date: resetDay })}
                  </Text>
                )}
              </Flex>
              {quotas.map((quota) => (
                <Flex key={quota.key} direction="column" gap="5px">
                  <Flex align="baseline" justify="space-between" gap={2} fontSize="12.5px">
                    <Text color="inkMuted">{t(`navigation.freePlanCard.quotas.${quota.key}`)}</Text>
                    <Text fontWeight="semibold" color="ink" fontVariantNumeric="tabular-nums">
                      {t('navigation.freePlanCard.counter', {
                        used: quota.used,
                        limit: quota.limit,
                      })}
                    </Text>
                  </Flex>
                  <QuotaBar quota={quota} colors={LIGHT_BAR} track="bg.muted" />
                </Flex>
              ))}
            </Flex>
            <Button
              h="34px"
              rounded="8px"
              bg="darkNav"
              color="white"
              fontSize="13px"
              fontWeight="semibold"
              _hover={{ bg: 'gray.700' }}
              onClick={onUpgrade}
            >
              {t('navigation.freePlanCard.cta')}
            </Button>
          </Flex>
        </Box>
      )}

      <chakra.button
        type="button"
        display="flex"
        w="full"
        flexDirection="column"
        gap="7px"
        textAlign="left"
        bg={open ? 'navDark.surfaceHoverBg' : 'navDark.surface'}
        borderWidth="1px"
        borderColor="navDark.surfaceBorder"
        rounded="12px"
        px="12px"
        py="11px"
        cursor="pointer"
        transition="background-color .15s"
        _hover={{ bg: 'navDark.surfaceHoverBg' }}
        _focusVisible={NAV_DARK_FOCUS_RING}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        // A click opens and never closes: the pointer has already opened the
        // panel on its way to the card. Leaving, Escape or focus moving away close it.
        onClick={show}
      >
        {/* The upgrade label is longer than the card in some languages: it drops
            under the plan name rather than overflowing. */}
        <Flex
          w="full"
          align="center"
          justify="space-between"
          wrap="wrap"
          columnGap={2}
          rowGap="4px"
        >
          <Text fontSize="13px" fontWeight="semibold" color="navDark.textStrong">
            {t('navigation.freePlanCard.title')}
          </Text>
          <Flex
            align="center"
            gap="3px"
            fontSize="12px"
            fontWeight="semibold"
            color="navDark.aiText"
          >
            {t('navigation.freePlanCard.upgrade')}
            <ArrowRight width={12} height={12} aria-hidden="true" />
          </Flex>
        </Flex>

        {top && (
          <>
            <Flex w="full" justify="space-between" gap={2} fontSize="11.5px">
              <Text color="navDark.textMuted">
                {t(`navigation.freePlanCard.quotas.${top.key}`)}
              </Text>
              <Text
                color="navDark.textStrong"
                fontWeight="semibold"
                fontVariantNumeric="tabular-nums"
              >
                {t('navigation.freePlanCard.counter', { used: top.used, limit: top.limit })}
              </Text>
            </Flex>
            <Box w="full">
              <QuotaBar quota={top} colors={DARK_BAR} track="navDark.badgeBg" />
            </Box>
          </>
        )}
      </chakra.button>
    </Box>
  );
};
